import React, { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'

import { NotificationCenter } from 'components/atoms/NotificationCenter'
import { useFeatureFlag } from 'hooks/useFeatureFlag'
import { Menu } from 'pages/components/Menu'
import { NavBar } from 'pages/components/NavBar'

export interface LayoutProps {
  children: React.ReactNode
  /** Initial open state (default: open). */
  menuOpenProp?: boolean
  /** When true, menu overlays content instead of shrinking the main column. */
  menuOverlay?: boolean
  /** Full-bleed main column (no card margin/padding) — review room, etc. */
  contentFlush?: boolean
}

/** LP compose/edit: menu closed by default and opens over the 3-column layout. */
export const landingPageComposeLayoutProps: Pick<
  LayoutProps,
  'menuOpenProp' | 'menuOverlay'
> = {
  menuOpenProp: false,
  menuOverlay: true
}

/** Review room: menu overlay + edge-to-edge content under the nav. */
export const reviewRoomLayoutProps: Pick<
  LayoutProps,
  'menuOpenProp' | 'menuOverlay' | 'contentFlush'
> = {
  menuOpenProp: false,
  menuOverlay: true,
  contentFlush: true
}

export const Layout = ({
  children,
  menuOpenProp = true,
  menuOverlay = false,
  contentFlush = false
}: LayoutProps) => {
  const [menuOpen, setMenuOpen] = useState<boolean>(menuOpenProp)
  const isAiTranslationsEnabled = useFeatureFlag('aiTranslations')

  useEffect(() => {
    setMenuOpen(menuOpenProp)
  }, [menuOpenProp])

  const handleOpenMenu = () => setMenuOpen(true)
  const handleCloseMenu = () => setMenuOpen(false)

  return (
    <Wrapper>
      <NavBar
        menuOpen={menuOpen}
        handleOpenMenu={handleOpenMenu}
        handleCloseMenu={handleCloseMenu}
      />
      <Content $scmenuopen={menuOpen ? 1 : 0} $overlay={menuOverlay}>
        <Menu
          menuOpen={menuOpen}
          overlay={menuOverlay}
          onClose={handleCloseMenu}
        />
        <Children
          $scmenuopen={menuOpen ? 1 : 0}
          $overlay={menuOverlay}
          $flush={contentFlush}
        >
          {children}
        </Children>
      </Content>

      {isAiTranslationsEnabled && <NotificationCenter />}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  height: 100%;
  width: 100%;
`
// @sieradz -  number handle warning in web browser: If you want to write it to the DOM, pass a string instead: scmenuopen="true" or scmenuopen={value.toString()}.
const Content = styled.div<{ $scmenuopen: 0 | 1; $overlay?: boolean }>`
  display: flex;
  min-height: calc(100vh - 64px);
  width: 100%;
  background: ${({ theme }) => theme.gradients.background};

  & > .MuiDrawer-root {
    transition-duration: 200ms;
    ${({ $overlay, $scmenuopen }) =>
      $overlay
        ? css`
            width: 0 !important;
            flex: 0 0 0 !important;
            overflow: visible;
          `
        : css`
            width: ${$scmenuopen ? '300px' : '0px'} !important;
            & > .MuiPaper-root {
              margin-top: 64px !important;
              height: calc(100% - 64px);
              width: ${$scmenuopen ? '300px' : '0px'} !important;
            }
          `}
  }
`
const Children = styled.div<{
  $scmenuopen: 0 | 1
  $overlay?: boolean
  $flush?: boolean
}>`
  transition-duration: 200ms;
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: ${({ $scmenuopen, $overlay }) =>
    $overlay ? '100%' : $scmenuopen ? 'calc(100% - 380px)' : '100%'} !important;
  padding: ${({ $flush }) => ($flush ? '0' : '48px')};
  margin: ${({ $flush }) => ($flush ? '0' : '20px 40px')};
  background-color: ${({ theme, $flush }) =>
    $flush ? 'transparent' : theme.colors.white};
  border-radius: ${({ $flush }) => ($flush ? '0' : '8px')};
  height: ${({ $flush }) => ($flush ? '100%' : 'fit-content')};
  min-height: ${({ $flush, theme }) =>
    $flush ? `calc(100vh - ${theme.dimensions.navBarHeight}px)` : 'auto'};
  max-height: ${({ $flush, theme }) =>
    $flush ? `calc(100vh - ${theme.dimensions.navBarHeight}px)` : 'none'};
  overflow: ${({ $flush }) => ($flush ? 'hidden' : 'visible')};
`

